import { axiosJSONInstance } from '@/config';
import {
  DownloadPickupLabel,
  PickupDataResponse,
  PickUpParams,
  PickupResponse,
  PickUpSingleResponse,
  PreferredTiming,
  ResponseType,
} from '@/types';

// Fetch Pickups
export const fetchPickups = async (
  page: number,
  pageSize?: number,
  search?: string,
): Promise<PickupResponse> => {
  const params = new URLSearchParams({
    page: page.toString(),
    perpage: (pageSize || 25).toString(),
  });
  if (search?.trim()) {
    params.append('search', search.trim());
  }
  const response = await axiosJSONInstance.get(`/merchants/pick-ups?${params.toString()}`);
  return response.data;
};

// Fetch RTO (Returns)
export const fetchRTO = async (
  page: number,
  pageSize?: number,
  search?: string,
): Promise<PickupResponse> => {
  const params = new URLSearchParams({
    page: page.toString(),
    perpage: (pageSize || 25).toString(),
  });
  if (search?.trim()) {
    params.append('search', search.trim());
  }
  const response = await axiosJSONInstance.get(`/merchants/rto?${params.toString()}`);
  return response.data;
};

// Fetch labels RTO CSV (Returns)
export const fetchLabelRTO_CSV = async (uid: string): Promise<File> => {
  const response = await axiosJSONInstance.get(`/merchants/rto/${uid}/statement-csv`, {
    responseType: 'blob',
    timeout: 600000, //10 minutes
  });
  return response.data;
};

// Fetch labels RTO PDF (Returns)
export const fetchLabelRTO_PDF = async (uid: string): Promise<File> => {
  const response = await axiosJSONInstance.get(`/merchants/rto/${uid}/statement`, {
    responseType: 'blob',
    timeout: 600000, //10 minutes
  });
  return response.data;
};

// Fetch RTO proof of delivery as PDF
export const fetchRTOProof = async (uid: string): Promise<File> => {
  const response = await axiosJSONInstance.get(`/merchants/rto/${uid}/proof`, {
    responseType: 'blob',
    timeout: 30000,
  });
  return response.data;
};

// Fetch Single Pickup By ID
export const fetchSinglePickupById = async (uid: string): Promise<PickUpSingleResponse> => {
  const response = await axiosJSONInstance.get(`/merchants/pick-ups/${uid}`);
  return response.data;
};

// Fetch Single RTO By ID
export const fetchSingleRTOById = async (uid: string): Promise<PickUpSingleResponse> => {
  const response = await axiosJSONInstance.get(`/merchants/rto/${uid}`);
  return response.data;
};

// request Pickup
export const requestPickup = async (data: PickUpParams): Promise<PickupDataResponse> => {
  const response = await axiosJSONInstance.post(`/merchants/pick-ups/request`, data);
  return response.data;
};

// bulk request Pickup. v1.1: the retried bulk ship (after the mandatory slot
// gate) may also carry the merchant's chosen preferred timing bucket
// (null = Anytime).
export const bulkRequestPickup = async (
  selectedIds: string[],
  timeSlotCode?: string,
  preferredTiming?: PreferredTiming | null,
): Promise<void> => {
  const queryParams: string[] = [];

  if (selectedIds?.length) {
    const idsQuery = selectedIds.map((id) => `filter_uids[]=${id}`).join('&');
    queryParams.push(idsQuery);
  }

  const queryString = queryParams.length ? `?${queryParams.join('&')}` : '';

  await axiosJSONInstance.post(`/merchants/pick-ups/bulk-request${queryString}`, {
    time_slot_code: timeSlotCode,
    ...(preferredTiming !== undefined ? { preferred_timing: preferredTiming } : {}),
  });
};

// bulk Cancel Pickup
export const bulkCancelPickup = async (selectedIds: string[]): Promise<void> => {
  const queryParams: string[] = [];

  if (selectedIds?.length) {
    const idsQuery = selectedIds.map((id) => `filter_uids[]=${id}`).join('&');
    queryParams.push(idsQuery);
  }

  const queryString = queryParams.length ? `?${queryParams.join('&')}` : '';

  await axiosJSONInstance.post(`/merchants/pick-ups/bulk-cancel${queryString}`);
};

// Cancel Pickup By ID
export const cancelPickupById = async (id: string): Promise<PickupResponse> => {
  const response = await axiosJSONInstance.post(`/merchants/pick-ups/${id}/cancel`);
  return response.data;
};

// Fetch labels Pickup by UID
export const fetchPickupLabel = async (uid: string): Promise<ResponseType<DownloadPickupLabel>> => {
  const response = await axiosJSONInstance.get(`/merchants/pick-ups/${uid}/label`);
  return response.data;
};

// generate labels Pickup by UID
export const generatePickupLabel = async (pickup_uid: string): Promise<void> => {
  await axiosJSONInstance.post(`/merchants/pick-ups/${pickup_uid}/label`);
};

// Fetch orders in a pickup (server-paginated — load-more, 25/batch)
export const fetchPickupOrders = async (pickupUid: string, page = 1, perpage = 25) => {
  const response = await axiosJSONInstance.get(`/merchants/pick-ups/${pickupUid}/orders`, {
    params: { page, perpage },
  });
  return response.data;
};

// Fetch orders in an RTO (server-paginated — load-more, 25/batch)
export const fetchRtoOrders = async (rtoUid: string, page = 1, perpage = 25) => {
  const response = await axiosJSONInstance.get(`/merchants/rto/${rtoUid}/orders`, {
    params: { page, perpage },
  });
  return response.data;
};
